<template>
  <div>
    <PageAllow controller="Purchases" action="getIntelligentPurchaseOrders" />
    <Breadcrumbs :items="items" />

    <v-sheet class="transparent-background-color">
      <v-row dense class="mx-5">
        <v-col cols="12" sm="12" md="4">
          <v-row dense align="center" class="pa-1">
            <SearcherDatetimeRangeWithRadio
              v-model="options"
              type="datetime"
              show-radios
              :radios="datetimeRadios"
              removeIcon
            />
          </v-row>
        </v-col>
        <v-col cols="12" sm="12" md="8" align-self="center">
          <v-row dense align="center" class="search-controls">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="px-0 py-0 caption font-weight-bold grey--text"
                  color="primary"
                  @click.stop="onSearch()"
                >
                  <v-icon small class="pe-1">fa-search</v-icon>
                  {{ $t("Search.search") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.searchTip") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="caption font-weight-bold grey--text"
                  @click.stop="resetSearch()"
                >
                  <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                  {{ $t("Search.reset") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.resetTip") }}</span>
            </v-tooltip>
            <v-divider vertical />
            <v-btn
              class="caption font-weight-bold grey--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
              small
              text
              @click.stop="$store.commit('toggleSearchPanel')"
            >
              {{ $t("Search.pro") }}
              <v-icon small class="ps-1">{{
                $store.state.showSearchPanel ? "fa-angle-up" : "fa-angle-down"
              }}</v-icon>
            </v-btn>
            <v-spacer />
            <v-btn
              v-if="
                $permission.permissionAllow(
                  'Purchases',
                  'getIntelligentPurchaseDetails'
                )
              "
              small
              color="primary"
              class="elevation-1 ms-1"
              width="110px"
              :disabled="!showSelected.length"
              @click="submit()"
            >
              <v-icon x-small class="pe-1">fa-plus</v-icon>
              {{ $t("PurchasesList.intelligent.purchase") }}
            </v-btn>
          </v-row>
        </v-col>
      </v-row>
      <v-expansion-panels
        v-model="$store.getters.panels"
        multiple
        flat
        accordion
      >
        <v-expansion-panel class="transparent-background-color">
          <v-expansion-panel-content>
            <v-row dense align="center">
              <v-col cols="12" sm="6" md="4">
                <SearcherKeyword
                  ref="order_code"
                  v-model="options.order_code"
                  @text="orderCode = $event"
                  :label="$t('PurchasesList.intelligent.order_code')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherKeyword
                  ref="key_word"
                  v-model="options.shop_keyword"
                  @text="shopKeyword = $event"
                  :label="
                    $t('PurchasesList.intelligent.shop_keyword', [
                      $consts.shopName(),
                    ])
                  "
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherDistrictsWithDescendants v-model="districtsSelected"/>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherStatus
                  v-model="options.status"
                  :label="$t('Order.status.title')"
                  :items="$consts.order.status.filter(
                    (element) =>
                      element.value == 1 ||
                      element.value == 2 ||
                      element.value == 3
                  )"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="caption grey--text input-chip-group-label" :class="$vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'">
                  {{ $t("PurchasesList.minusCurrentCount.title") }}
                </div>
                <v-radio-group row v-model="options.minus_current_count">
                  <v-radio
                    :label="$t('PurchasesList.minusCurrentCount.on')"
                    :key="1"
                    :value="1"
                  />
                  <v-radio
                    :label="$t('PurchasesList.minusCurrentCount.off')"
                    :key="0"
                    :value="0"
                  />
                </v-radio-group>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <div class="mx-4 my-4">
        <v-data-table
          v-model="showSelected"
          id="purchase_from_order_table"
          :headers="headers"
          class="elevation-2 pa-4 mt-4"
          :items="orders"
          show-select
          item-key="order_id"
          disable-sort
          :options.sync="options"
          :page.sync="paging.page"
          :server-items-length="paging.count"
          :footer-props="$t('$config.footer-props')"
          :no-data-text="$t('Stockouts.DetailNoData')"
          :loading="loading"
        >
          <template v-slot:item.created="{ item }">
            {{ item.created | formatTime }}
          </template>
        </v-data-table>
      </div>
    </v-sheet>
  </div>
</template>

<script>
import { mapState } from "vuex";
import i18n from "@/i18n";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import SearcherKeyword from "@/components/plugins/SearcherKeyword";
import Purchases from "@/api/purchases";
import SearcherDatetimeRangeWithRadio from "@/components/plugins/SearcherDatetimeRangeWithRadio";
import SearcherDistrictsWithDescendants from "@/components/plugins/SearcherDistrictsWithDescendants";
import SearcherStatus from "@/components/plugins/SearcherStatus";

const defaultOptions = {
  datetimeRadioSelected: "created",
  created_time_from:
    new Date(new Date().getTime() - 30 * 24 * 3600 * 1000)
      .toISOString()
      .substr(0, 10) + " 00:00:00",
  created_time_to: new Date().toISOString().substr(0, 10) + " 23:59:59",
  expect_time_from: "",
  expect_time_to: "",
  order_code: "",
  shop_keyword: "",
  district_ids: [],
  status: [1, 2, 3],
  minus_current_count: 1
};

export default {
  components: {
    PageAllow,
    Breadcrumbs,
    SearcherKeyword,
    SearcherDatetimeRangeWithRadio,
    SearcherDistrictsWithDescendants,
    SearcherStatus,
  },

  computed: {
    items() {
      var items = [
        {
          text: i18n.t("Nav.home"),
          disabled: false,
          href: "/home",
        },
        {
          text: i18n.t("Nav.purchases"),
          disabled: false,
          href: "/purchases",
        },
        {
          text: i18n.t("Nav.purchasesList"),
          disabled: false,
          href: "/purchases",
        },
        {
          text: i18n.t("PurchasesList.Fields.intelligent"),
          disabled: true,
          href: "",
        },
      ];

      return items;
    },

    headers() {
      var headers = [
        {
          text: i18n.t("PurchasesList.intelligent.Fields.order_code"),
          align: "start",
          value: "order_code",
        },
        {
          text: i18n.t("PurchasesList.intelligent.Fields.shop", [
            this.$consts.shopName(),
          ]),
          align: "start",
          value: "shop_name",
        },
        {
          text: i18n.t("PurchasesList.intelligent.Fields.created"),
          align: "start",
          value: "created",
        },
      ];

      return headers;
    },

    datetimeRadios: () => [
      {
        key: "created",
        text: i18n.t("PurchasesList.intelligent.created"),
      },
      {
        key: "expect",
        text: i18n.t("PurchasesList.intelligent.expect"),
      },
    ],
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["settings"]),
  },

  data: () => ({
    panels: [0],
    filter: "",
    mode: "",
    paging: {},
    showSelected: [],
    submitable: false,
    options: JSON.parse(
      sessionStorage.getItem("purchase_from_order-options")
        ? sessionStorage.getItem("purchase_from_order-options")
        : JSON.stringify(defaultOptions)
    ),
    orderCode: "",
    shopKeyword: "",
    districtsSelected: [],
    orders: [],
    loading: false,
  }),

  mounted() {
    this.$commonUtils
      .loadOptions("purchase_from_order", ["districtsSelected"])
      .then(
        function (data) {
          if (data.options) {
            this.options = data.options;
          } else this.resetSearch();

          data.selecteds.forEach((selected) => {
            switch (selected.key) {
              case "districtsSelected":
                this.districtsSelected = selected.value;
                break;
            }
          });
        }.bind(this)
      );
  },

  methods: {
    resetSearch() {
      this.shopKeyword = "";
      this.orderCode = "";
      this.districtsSelected = [];
      this.options = JSON.parse(JSON.stringify(defaultOptions));
    },
    onSearch() {
      this.options = Object.assign({}, this.options, {
        order_code: this.orderCode,
        shop_keyword: this.shop_keyword,
      });
    },
    getOrdersFromAPI() {
      this.loading = true;
      Purchases.fetchIntelligentPurchaseOrders(this.options).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.orders = response.data.orders;
            this.paging = response.data.paging;
          } else if (response.data.error.code.isNoResultSet()) {
            this.options.page = 1;
          }
          this.loading = false;
        }.bind(this)
      );
    },
    submit() {
      if (this.showSelected.length) {
        var orderIds = this.showSelected.map((s) => {
          return s.order_id;
        });
        this.$router.push({
          path: "/intelligent-purchase/intelligent-purchase-create",
          query: {
            order_ids: orderIds,
            type: this.$route.query.type,
            minus_current_count: this.options.minus_current_count
          },
        });
      }
    },
  },

  watch: {
    options: {
      deep: true,
      handler() {
        this.getOrdersFromAPI();
        sessionStorage.setItem(
          "purchase_from_order-options",
          JSON.stringify(this.options)
        );
      },
    },
    districtsSelected: {
      handler() {
        this.options.district_ids = this.districtsSelected
          ? this.districtsSelected.map((e) => {
              return e.id;
            })
          : [];
        sessionStorage.setItem(
          "purchase_from_order-districtsSelected",
          JSON.stringify(this.districtsSelected)
        );
      },
    },
  },
};
</script>